import {
  AfterViewChecked, AfterViewInit, Component,
  CUSTOM_ELEMENTS_SCHEMA, DestroyRef, ElementRef, inject, OnDestroy, OnInit, ViewChild
} from '@angular/core';
import {BehaviorSubject, combineLatest, concatAll, iif, Observable, of, ReplaySubject, Subject, switchMap, tap} from 'rxjs';
import {TranslatePipe, TranslateService} from '@ngx-translate/core';
import {catchError, distinctUntilChanged, map, takeUntil} from 'rxjs/operators';
import {ProductChange} from '../../shared/model/product-change';
import {ProductChangeService} from '../../shared/service/product-change/product-change.service';
import {ErrorInfo} from '../../shared/model/error-info';
import {ErrorHandlingService} from '../../shared/service/error-handling/error-handling.service';
import {ProductChangeOrder} from '../../shared/service/product-change/product-change-order';
import {FilterContext, FilterService} from '../../shared/service/filter/filter.service';
import {ProductChangeResponse} from '../../shared/model/product-change-response';
import {CdkFixedSizeVirtualScroll, CdkVirtualForOf, CdkVirtualScrollableWindow, CdkVirtualScrollViewport} from '@angular/cdk/scrolling';
import {PageState, PageStateService} from '../../shared/service/page-state/page-state.service';
import {AppStateService} from 'commons';
import {PersonalService} from '../../shared/service/personal/personal.service';
import {MatProgressSpinner} from '@angular/material/progress-spinner';
import {ErrorBoxComponent} from '../error-box/error-box.component';
import {MatButton} from '@angular/material/button';
import {AsyncPipe, NgClass, NgTemplateOutlet} from '@angular/common';
import {
  DacklFilterOffCanvasComponent,
  DacklFilterSingleSelectComponent,
  DacklFilterSingleSelectItem
} from '@datev/ngx-dackl';
import {MatSlideToggle, MatSlideToggleChange} from '@angular/material/slide-toggle';
import {FormsModule} from '@angular/forms';
import {SearchSlotSlimComponent} from '../search-slot-slim/search-slot-slim.component';
import {AdobeTrackingService} from '@datev/ngx-adobe-tracking';
import {FilterProductComponent} from '../filter-product/filter-product.component';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {BreakpointObserver, BreakpointState} from '@angular/cdk/layout';
import {ProductChangeCardComponent} from '../product-change-card/product-change-card.component';

import '@datev/dacks/content-header';


export interface ProductChangeRow {
  columns: ProductChange[];
}

export interface ProductChangesCardViewData {
  totalProductChanges: number;
  productChangesInView: number;
  flat: ProductChange[];
  rows: ProductChangeRow[];
}

export interface ProductChangeCardViewPageState extends PageState {
  sortOrder: ProductChangeOrder;
  selectedIndex: number;
  selectedPage: number;
  isPersonalFilter: boolean;
}

// Größe der Product-Change-Pakete, die vom BE geladen werden
const bucketSize = 100;
// Anzahl der Product-Changes auf einer Seite
const dataPageSize = 16;
// Offset beim Fokusieren eines Elements beim Browser-Back
const browserBackFocusPaddingDefault = 80;
const browserBackFocusPaddingDefaultMd = 280;
// Anzahl der Spalten in der MD-View
const mdListColumns = 2;

@Component({
    selector: 'myupdates-product-change-cardview',
    templateUrl: './product-change-cardview.component.html',
    styleUrls: ['./product-change-cardview.component.scss'],
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [DacklFilterSingleSelectComponent, CdkVirtualScrollViewport, CdkFixedSizeVirtualScroll,
        CdkVirtualScrollableWindow, CdkVirtualForOf, NgTemplateOutlet, MatButton, ErrorBoxComponent,
        NgClass, AsyncPipe, TranslatePipe, MatSlideToggle, FormsModule, MatProgressSpinner,
        SearchSlotSlimComponent, FilterProductComponent, DacklFilterOffCanvasComponent, ProductChangeCardComponent]
})
export class ProductChangeCardviewComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {

  private readonly destroyRef = inject(DestroyRef);

  @ViewChild(DacklFilterOffCanvasComponent)
  filterOffcanvasComponent?: DacklFilterOffCanvasComponent;

  @ViewChild(DacklFilterOffCanvasComponent, { read: ElementRef })
  filterOffCanvasElementRef!: ElementRef;

  @ViewChild('mdViewPort')
  mdViewPort?: CdkVirtualScrollViewport;

  @ViewChild('viewPort')
  viewPort?: CdkVirtualScrollViewport;

  public readonly mdScreenQuery: string = '(min-width: 992px)';

  viewModel$?: Observable<ProductChangesCardViewData>;
  errorInfo$ = new ReplaySubject<ErrorInfo>(1);
  dateTranslation = '';
  dateTranslationMonth = '';
  sortOrderSelections: Array<DacklFilterSingleSelectItem> = [];
  sortOrderSelection: ProductChangeOrder;
  pageState = {} as ProductChangeCardViewPageState;

  private totalNumberOfProductChanges?: number;
  public productChangeBuffer: ProductChange[] = [];
  private readonly dataPage$ = new BehaviorSubject<number>(0);
  private currentDataPage = 0;
  private focusIndex?: number | null;
  private businessPartnerFilterIsActive = false;

  private readonly destroy$ = new Subject<void>();

  filterOffcanvasShown = false;

  constructor(private readonly productChangeService: ProductChangeService,
              private readonly errorHandlingService: ErrorHandlingService,
              private readonly translate: TranslateService,
              private readonly filterService: FilterService,
              private readonly pageStateService: PageStateService,
              private readonly adobeTrackingService: AdobeTrackingService,
              private readonly personal: PersonalService,
              private readonly breakpointObserver: BreakpointObserver,
              readonly appState: AppStateService) {
    this.sortOrderSelection = this.productChangeService.sortOrderSelection;
    this.sortOrderSelections = [
      {label: this.translate.instant('product-change-cardview.sort_delivery_date'), value: ProductChangeOrder.DeliveryDateDesc},
      {label: this.translate.instant('product-change-cardview.sort_publishing_date'), value: ProductChangeOrder.PublishingDateDesc}
    ];
    this.dateTranslation = this.translate.instant('common.date_format');
    this.dateTranslationMonth = this.translate.instant('common.date_format_month');

    this.appState.isPersonalized$.pipe(
      takeUntil(this.destroy$),
      switchMap(isPersonalized =>
        iif(() => isPersonalized,
          combineLatest([this.personal.activeBusinessPartner$, this.appState.isPersonalProductChangeFilter$],
            (_activeBusinessPartner, isPersonalFilterActive) => isPersonalFilterActive),
          of(false))
      ),
      // Fehleranzeige: Zugriff auf Personalisierungsdaten
      catchError(err => {
        this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
        return of();
      })
    ).subscribe((isPersonalizedView) => {
      this.businessPartnerFilterIsActive = isPersonalizedView;
      this.refreshViewModel();
    });

    this.filterService.getSelectedProductFilter(FilterContext.ProductChange).pipe(
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.refreshViewModel();
    });

    this.pageStateService.beforeReplaceState.pipe(
      takeUntil(this.destroy$)
    ).subscribe(pageState => {
      if (pageState.anchorId) {
        this.pageState = this.pageStateService.pageState as ProductChangeCardViewPageState;
        this.pageState.sortOrder = this.sortOrderSelection;
        this.pageState.selectedPage = this.currentDataPage;
        this.pageState.selectedIndex = this.findIndex(pageState.anchorId);
      }
    });

    this.pageStateService.elementFocused.pipe(
      takeUntil(this.destroy$)
    ).subscribe(element => {
      let browserBackFocusPadding = browserBackFocusPaddingDefault;
      if (this.mdViewPort && this.checkVisibility(this.mdViewPort.elementRef.nativeElement)) {
        browserBackFocusPadding = browserBackFocusPaddingDefaultMd;
      }

      const elementTop = element.getBoundingClientRect().top;
      if (elementTop < browserBackFocusPadding) {
        window.scrollBy(0, elementTop - browserBackFocusPadding);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  ngOnInit(): void {
    this.pageState = this.pageStateService.pageState as ProductChangeCardViewPageState;
    if (this.pageState.anchorId) {
      this.focusIndex = this.pageState.selectedIndex;

      if (this.pageState.selectedPage > 0) {
        this.dataPage$.next(this.pageState.selectedPage);
      }
    }
    if (this.pageState.isPersonalFilter !== undefined) {
      this.appState.isPersonalProductChangeFilter = this.pageState.isPersonalFilter;
    } else {
      this.pageState.isPersonalFilter = this.appState.isPersonalProductChangeFilter;
    }

    this.breakpointObserver.observe([this.mdScreenQuery])
      .pipe(
        distinctUntilChanged((prev, curr) => prev.matches === curr.matches),
        takeUntil(this.destroy$) // Automatisch unsubscriben bei Zerstörung
      )
      .subscribe((state: BreakpointState) => {
        if (state.matches && this.filterOffcanvasComponent && this.filterOffcanvasShown) {
          this.filterOffcanvasShown = false;
          this.filterOffcanvasComponent.hideOffCanvas();
        }
      });
  }

  ngAfterViewChecked(): void {
    // Leider werden die Viewports erst zu diesem Zeitpunkt bestückt
    if (this.mdViewPort && this.viewPort && this.focusIndex) {

      let scrollIndex = this.focusIndex;
      const focusElementIndex = this.focusIndex;
      this.focusIndex = null;

      let renderViewPort = this.mdViewPort;
      if (this.checkVisibility(this.mdViewPort?.elementRef.nativeElement)) {
        renderViewPort = this.mdViewPort;
        scrollIndex = scrollIndex / mdListColumns;
      }

      requestAnimationFrame(() => {
        // Daten sind da, der nächste Repaint soll für das Scrolling zum richtigen Index genutzt werden.
        renderViewPort.scrollToIndex(scrollIndex, 'instant');
        this.findElement(focusElementIndex)?.focus();
      });
    }
  }

  ngAfterViewInit(): void {
    if (this.filterOffcanvasComponent) {
      this.filterOffcanvasComponent.hideFilter
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          tap(() => {
            // Erst, wenn das Feld sichtbar ist, kann z index gesetzt werden -> Change Detection triggern.
            setTimeout(() => {
              this.filterOffcanvasShown = false;
            });
          })
        )
        .subscribe();
    }
  }

  openfilterOffcanvas(): void {
    this.filterOffcanvasShown = true;
  }

  nextPage(): void {
    const lastValidIndex = this.findLastValidIndexOnCurrentPage();
    if (lastValidIndex) {
      this.focusIndex = lastValidIndex;
    }

    this.dataPage$.next(this.currentDataPage + 1);
  }

  private checkVisibility(element: HTMLElement): boolean {
    // checkVisibility() wird aktuell nicht vom Safari-Browser unterstützt und offsetParent schwer testbar,
    // deshalb folgende Alternativ-Implementierung
    const clientRect = element.getBoundingClientRect();
    return (clientRect.height !== 0);
  }

  private refreshViewModel(): void {
    this.resetViewModel();
    this.viewModel$ = this.getProductChanges().pipe(
      catchError(err => {
        this.errorInfo$.next(this.errorHandlingService.toErrorInfo(err));
        this.viewModel$ = of();
        return this.viewModel$;
      }));
  }

  private resetViewModel(): void {
    this.productChangeBuffer = [];
    this.currentDataPage = 0;
    this.totalNumberOfProductChanges = 0;
  }

  private loadProductChangesBucket(fromIndex: number): Observable<ProductChangeResponse> {
    return this.productChangeService.getProductChanges(bucketSize, fromIndex, this.sortOrderSelection,
      this.businessPartnerFilterIsActive);
  }

  loadProductChanges(toIndex: number): Observable<ProductChange[]> {
    let productChangeObservable: Observable<ProductChange[]> | null = null;

    if (this.totalNumberOfProductChanges && toIndex >= this.totalNumberOfProductChanges) {
      toIndex = this.totalNumberOfProductChanges - 1;
    }

    let currentTail = this.productChangeBuffer.length;
    if (toIndex >= currentTail) {
      // Nachladen
      // Eventuell müssen mehrere Buckets geladen werden (z.B. Browser-Back).
      while (currentTail <= toIndex) {
        const bucketObs = this.loadProductChangesBucket(currentTail).pipe(
          switchMap(bucket => {
            this.totalNumberOfProductChanges = bucket.totalItems;
            this.productChangeBuffer = this.productChangeBuffer.concat(bucket.productChanges);
            return of(this.productChangeBuffer.slice(0, toIndex + 1));
          })
        );
        currentTail += bucketSize;

        if (productChangeObservable) {
          // Müssen mehrere Buckets geladen werden, soll es nur einen Change auf das View-Model geben.
          productChangeObservable = productChangeObservable.pipe(map(_ => bucketObs), concatAll());
        } else {
          productChangeObservable = bucketObs;
        }
      }
    } else {
      // Index innerhalb des Puffers, deshalb kein Nachladen erforderlich.
      productChangeObservable = of(this.productChangeBuffer.slice(0, toIndex + 1));
    }

    return productChangeObservable ?? of<ProductChange[]>([]);
  }

  private getProductChanges(): Observable<ProductChangesCardViewData> {

    return this.dataPage$.pipe(
      switchMap(page => {

        const lastPage = Math.floor(((this.totalNumberOfProductChanges ?? 0) - 1) / dataPageSize);
        this.currentDataPage = page;
        if (this.currentDataPage > lastPage && lastPage >= 0) {
          this.currentDataPage = lastPage;
        }
        const toIndex = (this.currentDataPage + 1) * dataPageSize - 1;
        return this.loadProductChanges(toIndex);
      }),
      map(productChanges => {
        // Vorsicht: die Filterung erfolgt erst hier im View-Model, damit das tatsächliche Paging nicht durcheinander gerät.
        // Folge: der Index eines Eintrags im View-Model ist nicht notwendigerweise gleich dem Index im Daten-Cache.
        const filteredChanges = productChanges.filter((item: ProductChange) => !!item.id);

        return {
          totalProductChanges: this.totalNumberOfProductChanges,
          productChangesInView: productChanges.length,
          flat: filteredChanges,
          rows: this.chunkProductChanges(filteredChanges)
        } as ProductChangesCardViewData;
      }));
  }

  private chunkProductChanges(flat: ProductChange[]): ProductChangeRow[] {
    const rows: ProductChangeRow[] = [];

    for (let i = 0; i < flat.length; i += mdListColumns) {
      rows.push({ columns: flat.slice(i, i + mdListColumns ) });
    }

    return rows;
  }

  onClickCard(productChange: ProductChange): void {
    this.productChangeService.setLatestProductChange(productChange);
  }

  sortOrderChanged(): void {
    this.productChangeService.sortOrderSelection = this.sortOrderSelection;
    this.refreshViewModel();
  }

  toProductChange(entry: ProductChange): ProductChange {
    return entry;
  }

  private findIndex(anchorId: string): number {
    const mdPrefix = 'md:';
    if (anchorId.startsWith(mdPrefix)) {
      anchorId = anchorId.substring(mdPrefix.length);
    }

    return this.productChangeBuffer.findIndex(productChange => productChange.id === anchorId);
  }

  private findElement(index: number): HTMLElement | null {
    const id = this.productChangeBuffer[index].id;
    let element = document.getElementById(id);
    if (element && this.checkVisibility(element)) {
      return element;
    }

    element = document.getElementById('md:' + id);
    if (element && this.checkVisibility(element)) {
        return element;
    }

    return null;
  }

  private findLastValidIndexOnCurrentPage(): number | null {
    if (this.productChangeBuffer.length === 0) {
      return null;
    }

    let lastIndex = (this.currentDataPage + 1) * dataPageSize - 1;
    if (lastIndex >= this.productChangeBuffer.length) {
      lastIndex = this.productChangeBuffer.length - 1;
    }

    for (let index = lastIndex; index >= 0; index--) {
      const productChange = this.productChangeBuffer[index];
      if (productChange != null) {
        return index;
      }
    }

    return null;
  }

  changePersonalFilterState(event: MatSlideToggleChange): void {
    const isPersonalFilter = event.checked;
    this.appState.isPersonalProductChangeFilter = isPersonalFilter;
    this.adobeTrackingService.publishClickEvent('Programmänderungen:PersonalFilter:Toggle' +
      (isPersonalFilter ? 'Meine' : 'Alle'));
    this.pageState.isPersonalFilter = isPersonalFilter;
  }

  protected readonly FilterContext = FilterContext;

}
